import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import InventarServiceApi, { type InventarFotoMeta, type InventarKonfigurace } from "./InventarService";
import type {
    InventarKategorie,
    InventarPolozka,
    InventarHistorie,
    InventarPrirazeni,
    VystrojniKartaResponse,
    MojeVystrojResponse,
    CreateInventarPolozkaRequest,
    UpdateInventarPolozkaRequest,
    VydejInventarPolozkyRequest,
    VraceniInventarPolozkyRequest,
    InventarKontrolaStatusResponse,
    InventarPolozkaKontrola,
    CreateInventarKontrolaRequest,
    InventuraRequest,
    SkenPolozkyRequest,
    NalezenaPolozkaRequest,
    InventarInventura,
    InventarInventuraPolozka,
} from "../generated/models/Inventar";

// ==========================================
// KATEGORIE QUERIES & MUTATIONS
// ==========================================

export const useInventarKategorieQuery = () => {
    return useQuery<InventarKategorie[], Error>({
        queryKey: ["inventarKategorie"],
        queryFn: () => InventarServiceApi.getAllKategorie(),
        staleTime: 5 * 60 * 1000,
    });
};

export const useCreateKategorieMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<InventarKategorie, Error, Partial<InventarKategorie>>({
        mutationFn: (data) => InventarServiceApi.createKategorie(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarKategorie"] });
            showToast("Kategorie byla vytvořena.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit kategorii.", "error");
        },
    });
};

export const useUpdateKategorieMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; data: Partial<InventarKategorie> }>({
        mutationFn: ({ id, data }) => InventarServiceApi.updateKategorie(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarKategorie"] });
            showToast("Kategorie byla aktualizována.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat kategorii.", "error");
        },
    });
};

export const useReorderKategorieMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number[]>({
        mutationFn: (orderedIds) => InventarServiceApi.reorderKategorie(orderedIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarKategorie"] });
            showToast("Pořadí uloženo.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se změnit pořadí.", "error");
        },
    });
};

export const useDeleteKategorieMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => InventarServiceApi.deleteKategorie(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarKategorie"] });
            showToast("Kategorie byla smazána.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat kategorii.", "error");
        },
    });
};

// ==========================================
// TYPY VELIKOSTÍ
// ==========================================

export const useVelikostTypyQuery = () => {
    return useQuery<any[], Error>({
        queryKey: ["velikostTypy"],
        queryFn: () => InventarServiceApi.getAllVelikostTypy(),
        staleTime: 5 * 60 * 1000,
    });
};

export const useCreateVelikostTypMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<any, Error, any>({
        mutationFn: (data) => InventarServiceApi.createVelikostTyp(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["velikostTypy"] });
            showToast("Typ velikosti vytvořen.", "success");
        },
        onError: (error) => showToast(error.message || "Chyba.", "error"),
    });
};

export const useUpdateVelikostTypMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<void, Error, { id: number; data: any }>({
        mutationFn: ({ id, data }) => InventarServiceApi.updateVelikostTyp(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["velikostTypy"] });
            showToast("Typ velikosti aktualizován.", "success");
        },
        onError: (error) => showToast(error.message || "Chyba.", "error"),
    });
};

export const useDeleteVelikostTypMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<void, Error, number>({
        mutationFn: (id) => InventarServiceApi.deleteVelikostTyp(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["velikostTypy"] });
            showToast("Typ velikosti smazán.", "success");
        },
        onError: (error) => showToast(error.message || "Chyba.", "error"),
    });
};

// ==========================================
// POLOŽKY QUERIES
// ==========================================

export const useAllInventarPolozkyQuery = (
    params?: { kategorieId?: number; vozidloId?: number; clenId?: number },
    options?: { enabled?: boolean }
) => {
    return useQuery<InventarPolozka[], Error>({
        queryKey: ["inventarPolozky", params],
        queryFn: () => InventarServiceApi.getAllPolozky(params),
        staleTime: 5 * 60 * 1000,
        enabled: options?.enabled ?? true,
    });
};

export const useInventarPolozkaByIdQuery = (id: number) => {
    return useQuery<InventarPolozka, Error>({
        queryKey: ["inventarPolozky", id],
        queryFn: () => InventarServiceApi.getPolozkaById(id),
        enabled: !!id,
    });
};

export const useInventarHistorieQuery = (polozkaId: number | null) => {
    return useQuery<InventarHistorie[], Error>({
        queryKey: ["inventarHistorie", polozkaId],
        queryFn: () => InventarServiceApi.getHistorie(polozkaId!),
        enabled: !!polozkaId,
    });
};

export const useInventarPrirazeniQuery = (polozkaId: number | null) => {
    return useQuery<InventarPrirazeni[], Error>({
        queryKey: ["inventarPrirazeni", polozkaId],
        queryFn: () => InventarServiceApi.getPrirazeniHistorie(polozkaId!),
        enabled: !!polozkaId,
    });
};

export const useCekajiciNaInvCisloQuery = (options?: { enabled?: boolean }) => {
    return useQuery<InventarPolozka[], Error>({
        queryKey: ["inventarCekajiciNaCislo"],
        queryFn: () => InventarServiceApi.getCekajiciNaInvCislo(),
        enabled: options?.enabled ?? true,
    });
};

// ==========================================
// POLOŽKY MUTATIONS
// ==========================================

export const useCreateInventarPolozkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<InventarPolozka, Error, CreateInventarPolozkaRequest>({
        mutationFn: (data) => InventarServiceApi.createPolozka(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarPolozky"] });
            queryClient.invalidateQueries({ queryKey: ["inventarKategorie"] });
            showToast("Položka inventáře byla vytvořena.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit položku.", "error");
        },
    });
};

export const useUpdateInventarPolozkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<InventarPolozka, Error, { id: number; data: UpdateInventarPolozkaRequest }>({
        mutationFn: ({ id, data }) => InventarServiceApi.updatePolozka(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarPolozky"] });
            showToast("Položka inventáře byla aktualizována.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat položku.", "error");
        },
    });
};

export const useDeleteInventarPolozkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; duvod?: string }>({
        mutationFn: ({ id, duvod }) => InventarServiceApi.deletePolozka(id, duvod),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarPolozky"] });
            queryClient.invalidateQueries({ queryKey: ["inventarKategorie"] });
            showToast("Položka byla vyřazena.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se vyřadit položku.", "error");
        },
    });
};

// ==========================================
// FOTO
// ==========================================

const fotkyKey = (polozkaId: number) => ["inventarFotky", polozkaId] as const;

export const useInventarFotkyQuery = (polozkaId: number) =>
    useQuery({
        queryKey: fotkyKey(polozkaId),
        queryFn: () => InventarServiceApi.getFotky(polozkaId),
        enabled: !!polozkaId,
    });

/**
 * ZAMERNE bez `onError` toastu i bez invalidace v `onSuccess`: nahrava se po
 * jednom v davce, takze obojí patri az za celou davku do komponenty. Toast tady
 * by u tri vadnych souboru vyskocil ctyrikrat (trikrat za soubor a jednou
 * souhrn) a invalidace by pri peti fotkach vyvolala pet refetchu seznamu misto
 * jednoho. Invalidaci dela `useInvalidovatInventarFotky`.
 */
export const useUploadInventarFotografieMutation = (polozkaId: number) =>
    useMutation<InventarFotoMeta, Error, { soubor: Blob; nazev: string }>({
        mutationFn: ({ soubor, nazev }) => InventarServiceApi.uploadFotografie(polozkaId, soubor, nazev),
    });

/** Prenacte seznam fotek — vola se jednou za celou davku nahrani. */
export const useInvalidovatInventarFotky = (polozkaId: number) => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: fotkyKey(polozkaId) });
};

export const useDeleteInventarFotografieMutation = (polozkaId: number) => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (fotoId) => InventarServiceApi.deleteFotografie(polozkaId, fotoId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: fotkyKey(polozkaId) });
            showToast("Fotografie byla smazána.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat fotografii.", "error");
        },
    });
};

// ==========================================
// VÝDEJ / VRÁCENÍ MUTATIONS
// ==========================================

export const useVydejInventarPolozkuMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<InventarPrirazeni, Error, { polozkaId: number; data: VydejInventarPolozkyRequest }>({
        mutationFn: ({ polozkaId, data }) => InventarServiceApi.vydejPolozku(polozkaId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarPolozky"] });
            queryClient.invalidateQueries({ queryKey: ["inventarPrirazeni"] });
            queryClient.invalidateQueries({ queryKey: ["vystrojniKarta"] });
            queryClient.invalidateQueries({ queryKey: ["mojeVybaveni"] });
            showToast("Položka byla vydána.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se vydat položku.", "error");
        },
    });
};

export const useVratInventarPolozkuMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { polozkaId: number; data: VraceniInventarPolozkyRequest }>({
        mutationFn: ({ polozkaId, data }) => InventarServiceApi.vratPolozku(polozkaId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarPolozky"] });
            queryClient.invalidateQueries({ queryKey: ["inventarPrirazeni"] });
            queryClient.invalidateQueries({ queryKey: ["vystrojniKarta"] });
            queryClient.invalidateQueries({ queryKey: ["mojeVybaveni"] });
            showToast("Položka byla vrácena.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se vrátit položku.", "error");
        },
    });
};

// ==========================================
// VÝSTROJNÍ KARTA
// ==========================================

export const useVystrojniKartaQuery = (uzivatelId: number | null) => {
    return useQuery<VystrojniKartaResponse, Error>({
        queryKey: ["vystrojniKarta", uzivatelId],
        queryFn: () => InventarServiceApi.getVystrojniKarta(uzivatelId!),
        enabled: !!uzivatelId,
    });
};

// ==========================================
// KONFIGURACE INVENTÁRNÍCH ČÍSEL
// ==========================================

export const useInventarKonfiguraceQuery = () => {
    return useQuery<InventarKonfigurace, Error>({
        queryKey: ["inventarKonfigurace"],
        queryFn: () => InventarServiceApi.getKonfigurace(),
    });
};

export const useUpdateInventarKonfiguraceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { cisloZdroj: string; cisloFormat?: string; cisloSekvencePer?: string; cisloPovinne?: boolean; notifikaceOopEnabled?: boolean | null }>({
        mutationFn: (data) => InventarServiceApi.updateKonfigurace(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarKonfigurace"] });
            showToast("Konfigurace inventárních čísel uložena.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se uložit konfiguraci.", "error");
        },
    });
};

export const useDoplnInventarniCislaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { polozky: Array<{ polozkaId: number; inventarniCislo: string; datumZarazeniUctarna?: string; ucetniKategorie?: string }> }>({
        mutationFn: (data) => InventarServiceApi.doplnInventarniCisla(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventarPolozky"] });
            queryClient.invalidateQueries({ queryKey: ["inventarCekajiciNaCislo"] });
            showToast("Inventární čísla byla doplněna.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se doplnit čísla.", "error");
        },
    });
};

// ==========================================
// INVENTURA
// ==========================================

export const useInventuryQuery = () => {
    return useQuery<InventarInventura[], Error>({
        queryKey: ["inventury"],
        queryFn: () => InventarServiceApi.getInventury(),
    });
};

export const useInventuraQuery = (id: number | null) => {
    return useQuery<InventarInventura, Error>({
        queryKey: ["inventura", id],
        queryFn: () => InventarServiceApi.getInventura(id!),
        enabled: !!id,
        // Souběžné skenování z více míst - dokud inventura probíhá, refreshuj i bez akce uživatele
        refetchInterval: (query) => (query.state.data?.stav === 'PROBIHAJICI' ? 10000 : false),
    });
};

export const usePolozkyInventuryQuery = (
    inventuraId: number | null,
    options?: { refetchInterval?: number | false }
) => {
    return useQuery<InventarInventuraPolozka[], Error>({
        queryKey: ["inventuraPolozky", inventuraId],
        queryFn: () => InventarServiceApi.getPolozkyInventury(inventuraId!),
        enabled: !!inventuraId,
        refetchInterval: options?.refetchInterval ?? false,
    });
};

export const useZahajitInventuruMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<InventarInventura, Error, InventuraRequest>({
        mutationFn: (data) => InventarServiceApi.zahajitInventuru(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inventury"] });
            showToast("Inventura zahájena.", "success");
        },
        onError: (error) => showToast(error.message || "Nepodařilo se zahájit inventuru.", "error"),
    });
};

export const useDokoncitInventuruMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<InventarInventura, Error, number>({
        mutationFn: (id) => InventarServiceApi.dokoncitInventuru(id),
        onSuccess: (_data, id) => {
            queryClient.invalidateQueries({ queryKey: ["inventury"] });
            queryClient.invalidateQueries({ queryKey: ["inventura", id] });
            queryClient.invalidateQueries({ queryKey: ["inventuraPolozky", id] });
            showToast("Inventura dokončena.", "success");
        },
        onError: (error) => showToast(error.message || "Nepodařilo se dokončit inventuru.", "error"),
    });
};

export const useSkenPolozkyMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<{ message: string }, Error, { inventuraId: number; data: SkenPolozkyRequest }>({
        mutationFn: ({ inventuraId, data }) => InventarServiceApi.skenPolozky(inventuraId, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["inventura", variables.inventuraId] });
            queryClient.invalidateQueries({ queryKey: ["inventuraPolozky", variables.inventuraId] });
            showToast("Položka zaznamenána.", "success");
        },
        onError: (error) => showToast(error.message || "Položku se nepodařilo zaznamenat.", "error"),
    });
};

export const useOznacNalezenaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation<{ message: string }, Error, { inventuraId: number; polozkaId: number; data?: NalezenaPolozkaRequest }>({
        mutationFn: ({ inventuraId, polozkaId, data }) => InventarServiceApi.oznacNalezena(inventuraId, polozkaId, data ?? {}),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["inventura", variables.inventuraId] });
            queryClient.invalidateQueries({ queryKey: ["inventuraPolozky", variables.inventuraId] });
        },
        onError: (error) => showToast(error.message || "Položku se nepodařilo označit jako nalezenou.", "error"),
    });
};

// ==========================================
// STATISTIKY
// ==========================================

export const useInventarStatistikyQuery = () => {
    return useQuery<{ aktivnichPolozek: number; cekajicichNaCislo: number }, Error>({
        queryKey: ["inventarStatistiky"],
        queryFn: () => InventarServiceApi.getStatistiky(),
        staleTime: 5 * 60 * 1000,
    });
};

// ==========================================
// REVIZE OOP (kontroly položek)
// ==========================================

export const useKontrolyForPolozkaQuery = (polozkaId: number | null) =>
    useQuery<InventarKontrolaStatusResponse[], Error>({
        queryKey: ['inventarKontroly', polozkaId],
        queryFn: () => InventarServiceApi.getKontrolyForPolozka(polozkaId!),
        enabled: !!polozkaId,
    });

export const useInventarKontrolyHistorieQuery = (polozkaId: number | null, typId: number | null) =>
    useQuery<InventarPolozkaKontrola[], Error>({
        queryKey: ['inventarKontrolyHistorie', polozkaId, typId],
        queryFn: () => InventarServiceApi.getKontrolyHistorie(polozkaId!, typId!),
        enabled: !!polozkaId && !!typId,
    });

export const useCreatePolozkaKontrolaMutation = () => {
    const qc = useQueryClient();
    const { showToast } = useToast();
    return useMutation<InventarPolozkaKontrola, Error, { polozkaId: number; data: CreateInventarKontrolaRequest }>({
        mutationFn: ({ polozkaId, data }) => InventarServiceApi.createPolozkaKontrola(polozkaId, data),
        onSuccess: (_d, v) => {
            qc.invalidateQueries({ queryKey: ['inventarKontroly', v.polozkaId] });
            qc.invalidateQueries({ queryKey: ['inventarKontrolyHistorie', v.polozkaId] });
            showToast('Revize zaznamenána', 'success');
        },
        onError: (e) => showToast(e.message || 'Chyba při zápisu revize', 'error'),
    });
};

export const useDeletePolozkaKontrolaMutation = () => {
    const qc = useQueryClient();
    const { showToast } = useToast();
    return useMutation<unknown, Error, { polozkaId: number; kontrolaId: number }>({
        mutationFn: ({ polozkaId, kontrolaId }) => InventarServiceApi.deletePolozkaKontrola(polozkaId, kontrolaId),
        onSuccess: (_d, v) => {
            qc.invalidateQueries({ queryKey: ['inventarKontroly', v.polozkaId] });
            qc.invalidateQueries({ queryKey: ['inventarKontrolyHistorie', v.polozkaId] });
            showToast('Revize smazána', 'success');
        },
        onError: (e) => showToast(e.message || 'Chyba při mazání revize', 'error'),
    });
};

// ==========================================
// HROMADNÝ TISK / STAŽENÍ PDF
// ==========================================

export const useDownloadVystrojniKartaPdfMutation = () => {
    const { showToast } = useToast();
    return useMutation<void, Error, number>({
        mutationFn: (uzivatelId) => InventarServiceApi.downloadVystrojniKartaPdf(uzivatelId),
        onError: (error) => {
            showToast(error.message || "Nepodařilo se stáhnout PDF výstrojní karty.", "error");
        },
    });
};

export const useDownloadTiskPdfMutation = () => {
    const { showToast } = useToast();
    return useMutation<void, Error, { rezim: 'QR' | 'SEZNAM'; ids?: number[]; kategorieId?: number; format?: string }>({
        mutationFn: ({ rezim, ids, kategorieId, format }) => InventarServiceApi.downloadTiskPdf(rezim, ids, kategorieId, format),
        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytisknout PDF.", "error");
        },
    });
};

// ==========================================
// MOJE VYBAVENÍ (member self-service)
// ==========================================

export const useMojeVybaveniQuery = () =>
    useQuery<MojeVystrojResponse, Error>({
        queryKey: ['mojeVybaveni'],
        queryFn: () => InventarServiceApi.getMojeVybaveni(),
    });
